import { useState, useEffect, useCallback } from 'react'
import { useVestigioService } from 'hooks/useVestigioService'
import { useSolicitacaoVestigioService } from 'hooks/useSolicitacaoVestigioService'
import { useSetorService } from 'hooks/useSetorService'
import { useAuth } from 'hooks/useAuth'
import useRouter from 'utils/useRouter'
import constants from 'utils/constants'

export const useCentralDeCustodia = (props) => {
  const { route } = props
  const router = useRouter()
  const { socket, currentUser, verificarSetor } = useAuth()

  const setorService = useSetorService()
  const vestigioService = useVestigioService()
  const solicitacaoVestigioService = useSolicitacaoVestigioService()

  const [abaSelecionada, setAbaSelecionada] = useState(0)
  const [setores, setSetores] = useState([])

  const [vestigiosNoSetor, setVestigiosNoSetor] = useState([])
  const [vestigiosEncaminhados, setVestigiosEncaminhados] = useState([])
  const [vestigiosParaEntrega, setVestigiosParaEntrega] = useState([])
  const [vestigiosParaArmazenar, setVestigiosParaArmazenar] = useState([])
  const [vestigiosParaDescarte, setVestigiosParaDescarte] = useState([])
  const [solicitacoesVestigio, setSolicitacoesVestigio] = useState([])

  const [processandoNoSetor, setProcessandoNoSetor] = useState(true)
  const [processandoEncaminhados, setProcessandoEncaminhados] = useState(true)
  const [processandoParaEntrega, setProcessandoParaEntrega] = useState(true)
  const [processandoParaArmazenar, setProcessandoParaArmazenar] = useState(true)
  const [processandoParaDescarte, setProcessandoParaDescarte] = useState(true)
  const [processandoSolicitacoes, setProcessandoSolicitacoes] = useState(true)
  const [processandoStatusSolicitacao, setProcessandoStatusSolicitacao] = useState(false)
  const [loading, setLoading] = useState(false)

  // Segurança de setor
  useEffect(() => {
    if (!verificarSetor([184])) {
      router.history.push('/')
    }
  }, [currentUser, verificarSetor, router.history])

  const fetchVestigiosParaDescarte = useCallback(async () => {
    setProcessandoParaDescarte(true)
    const response = await vestigioService.listarVestigiosNaCentralDeCustodiaPorStatus(
      constants.STATUS_CENTRAL_CUSTODIA.PARA_DESCARTE,
      setProcessandoParaDescarte
    )
    setVestigiosParaDescarte(response || [])
    setProcessandoParaDescarte(false)
  }, [])

  const fetchVestigiosParaEntrega = useCallback(async () => {
    setProcessandoParaEntrega(true)
    const response = await vestigioService.listarVestigiosNaCentralDeCustodiaPorStatus(
      constants.STATUS_CENTRAL_CUSTODIA.PARA_ENTREGA,
      setProcessandoParaEntrega
    )
    setVestigiosParaEntrega(response || [])
    setProcessandoParaEntrega(false)
  }, [])

  const fetchVestigiosParaArmazenar = useCallback(async () => {
    setProcessandoParaArmazenar(true)
    const response = await vestigioService.listarVestigiosNaCentralDeCustodiaPorStatus(
      constants.STATUS_CENTRAL_CUSTODIA.PARA_ARMAZENAMENTO,
      setProcessandoParaArmazenar
    )
    setVestigiosParaArmazenar(response || [])
    setProcessandoParaArmazenar(false)
  }, [])

  const fetchVestigiosNoSetor = useCallback(async () => {
    setProcessandoNoSetor(true)
    const response = await vestigioService.listarVestigiosNaCentralDeCustodiaPorStatus('TODOS', setProcessandoNoSetor)
    setVestigiosNoSetor(response)
    setProcessandoNoSetor(false)
  }, [])

  const fetchVestigiosEncaminhados = useCallback(async () => {
    setProcessandoEncaminhados(true)
    const response = await vestigioService.listarVestigiosNaCentralDeCustodiaPorStatus('ENCAMINHADOS', setProcessandoEncaminhados)
    setVestigiosEncaminhados(response || [])
    setProcessandoEncaminhados(false)
  }, [])

  const fetchSetores = useCallback(async () => {
    const response = await setorService.listarSetoresAsync()
    setSetores(response || [])
  }, [])

  const fetchSolicitacoesVestigio = useCallback(async () => {
    setProcessandoSolicitacoes(true)
    const responseSolicitacoes = await solicitacaoVestigioService.listarSolicitacoesVestigioCentralCustodia()
    setSolicitacoesVestigio(responseSolicitacoes || [])
    setProcessandoSolicitacoes(false)
  }, [])

  // Socket
  useEffect(() => {
    if (socket) {
      socket.on('atualizarSolicitacoes', () => {
        fetchSolicitacoesVestigio()
      })
    }
    return () => {
      if (socket) {
        socket.off('atualizarSolicitacoes')
      }
    }
  }, [socket, fetchSolicitacoesVestigio])

  // Carga sob mudança de abas
  useEffect(() => {
    if (abaSelecionada === 0 && !solicitacoesVestigio?.length) fetchSolicitacoesVestigio()
    if (abaSelecionada === 1 && !vestigiosEncaminhados?.length) fetchVestigiosEncaminhados()
    if (abaSelecionada === 2 && !vestigiosParaArmazenar?.length) fetchVestigiosParaArmazenar()
    if (abaSelecionada === 3 && !vestigiosParaEntrega?.length) fetchVestigiosParaEntrega()
    if (abaSelecionada === 4 && !vestigiosParaDescarte?.length) fetchVestigiosParaDescarte()
  }, [
    abaSelecionada,
    solicitacoesVestigio?.length,
    vestigiosEncaminhados?.length,
    vestigiosParaArmazenar?.length,
    vestigiosParaEntrega?.length,
    fetchSolicitacoesVestigio,
    fetchVestigiosEncaminhados,
    fetchVestigiosParaArmazenar,
    fetchVestigiosParaEntrega,
    fetchVestigiosParaDescarte,
  ])

  // Cargas iniciais e recorrentes básicas
  useEffect(() => {
    fetchVestigiosParaArmazenar()
    fetchVestigiosParaEntrega()
    fetchVestigiosEncaminhados()
    fetchVestigiosNoSetor()
    fetchSetores()
  }, [
    fetchVestigiosParaArmazenar,
    fetchVestigiosParaEntrega,
    fetchVestigiosEncaminhados,
    fetchVestigiosNoSetor,
    fetchVestigiosParaDescarte,
    fetchSetores,
  ])

  const handleChangeAba = useCallback((event, newValue) => {
    setAbaSelecionada(newValue)
  }, [])

  const handleRowClick = useCallback((rowData) => {
    router.history.push(`/casos/caso/vestigio/detalhar/${rowData.id}`)
  }, [router.history])

  const handleAceitarVestigio = useCallback(async (rowData) => {
    try {
      setLoading(true)
      await vestigioService.aceitarVestigio(rowData.id)
      await Promise.all([
        fetchVestigiosEncaminhados(),
        fetchVestigiosNoSetor(),
        fetchVestigiosParaEntrega(),
      ])
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [fetchVestigiosEncaminhados, fetchVestigiosNoSetor, fetchVestigiosParaEntrega])

  const handleNegarVestigio = useCallback(async (rowData) => {
    try {
      setLoading(true)
      await vestigioService.negarVestigio(rowData.id)
      await Promise.all([
        fetchVestigiosEncaminhados(),
        fetchVestigiosNoSetor(),
        fetchVestigiosParaEntrega(),
      ])
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [fetchVestigiosEncaminhados, fetchVestigiosNoSetor, fetchVestigiosParaEntrega])

  const handleAlterarStatusSolicitacao = useCallback(async (idSolicitacao, novoStatus) => {
    try {
      setProcessandoStatusSolicitacao(true)
      await solicitacaoVestigioService.alterarStatusSolicitacao(
        idSolicitacao,
        novoStatus,
        setProcessandoStatusSolicitacao
      )
      await fetchSolicitacoesVestigio()
    } catch (error) {
      console.error(error)
    } finally {
      setProcessandoStatusSolicitacao(false)
    }
  }, [fetchSolicitacoesVestigio])

  return {
    abaSelecionada,
    setAbaSelecionada,
    setores,
    vestigiosNoSetor,
    vestigiosEncaminhados,
    vestigiosParaEntrega,
    vestigiosParaArmazenar,
    vestigiosParaDescarte,
    solicitacoesVestigio,
    processandoNoSetor,
    processandoEncaminhados,
    processandoParaEntrega,
    processandoParaArmazenar,
    processandoSolicitacoes,
    processandoParaDescarte,
    processandoStatusSolicitacao,
    loading,
    handleChangeAba,
    handleRowClick,
    handleAceitarVestigio,
    handleNegarVestigio,
    handleAlterarStatusSolicitacao,
    router,
  }
}
